/**
 * Customization (order) endpoints: the paginated query by status, the report
 * export and the update.
 */
import { z } from 'zod';
import { api, fetchBlob } from '@/shared/api/client';
import { queryOptions } from '@tanstack/react-query';
import { STORE_ALL, type Platform } from '@/shared/constants';
import type { StoreId } from '@/shared/config/app-config';

type Scalar = number | string | null | undefined;
const scalar = z.union([z.number(), z.string()]).nullish();

/**
 * The order is the one document the client sends back whole (`PUT
 * customization`), and the API `$set`s `parts`, `props`,
 * `statusLogs` and `product` as a whole: the order and its nested objects
 * keep the fields they do not name (`.loose()`) so nothing is lost on the
 * round trip. Every other API schema of the client strips unknown fields.
 */
const measurementSchema = z
  .object({
    name: z.string(),
    letter: z.string().nullish(),
    value: scalar,
    min: z.number().nullish(),
    max: z.number().nullish(),
    totalMeasurement: z.boolean().nullish(),
    totalValue: scalar,
    comments: z.string().nullish(),
    pictures: z.array(z.string()).nullish(),
  })
  .loose();
export type Measurement = z.infer<typeof measurementSchema>;

const partModelDataSchema = z
  .object({
    productLine: z.string().nullish(),
    model: z.string().nullish(),
    pantsModel: z.string().nullish(),
    sewingPatterns: z.array(z.string()).nullish(),
    sewingPatternObjects: z
      .array(z.object({ id: z.string().nullish(), name: z.string() }).loose())
      .nullish(),
    darkPicture: z.string().nullish(),
  })
  .loose();
export type PartModelData = z.infer<typeof partModelDataSchema>;

const partSelectedSchema = z
  .object({
    measureType: z.string().nullish(),
    size: scalar,
    sizePants: scalar,
    FABRIC: z.unknown().optional(),
    OPTION: z
      .array(
        z
          .object({ data: z.object({ name: z.string().nullish() }).nullish() })
          .loose(),
      )
      .nullish(),
    embroideryType: z.string().nullish(),
    embroideryColor: z.string().nullish(),
    embroideryPosition: z.string().nullish(),
    embroideryText: z.string().nullish(),
    embroideryTextDots: z.unknown().optional(),
  })
  .loose();
export type PartSelected = z.infer<typeof partSelectedSchema>;

const statusLogSchema = z
  .object({
    currentStatusId: z.number().nullish(),
    createdAt: z.string().nullish(),
    username: z.string().nullish(),
  })
  .loose();

const orderPartSchema = z
  .object({
    id: z.string(),
    /** Absent until the order enters production (a pending order carries parts with no status). */
    customisationStatusId: z.number().nullish(),
    model: z.object({ data: partModelDataSchema }).loose(),
    selected: partSelectedSchema,
    measurements: z.array(measurementSchema).nullish(),
    statusLogs: z.array(statusLogSchema).nullish(),
    sku: z.string().nullish(),
    count: scalar,
    price: scalar,
    localPrice: scalar,
    oaCode: z.string().nullish(),
    qualityIssue: z.boolean().nullish(),
    supplierExpectedDeliveryDate: z.string().nullish(),
    expectedDeliveryDate: z.string().nullish(),
    comment: z.string().nullish(),
  })
  .loose();
export type OrderPart = z.infer<typeof orderPartSchema>;

/** The product snapshot of an order, as the report and the details read it. */
const orderProductSchema = z
  .object({
    line: z.string().nullish(),
    size: scalar,
    hasUniqueSize: z.boolean().nullish(),
    sizes: z
      .array(z.object({ size: scalar, m3SkuStock: scalar }).loose())
      .nullish(),
    textProps: z.object({ colors: z.unknown().optional() }).loose().nullish(),
  })
  .loose();

/** An order as the API projects it for the lists; the details use many more fields. */
export const orderSchema = z
  .object({
    id: z.string(),
    displayId: z.string().nullish(),
    customisationStatusId: z.number(),
    previousStatusId: z.number().nullish(),
    platform: z.string().nullish(),
    createdAt: z.string().nullish(),
    timezoneOffset: z.number().nullish(),
    priority: z.union([z.boolean(), z.string()]).nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    sellerName: z.string().nullish(),
    productName: z.string().nullish(),
    productReference: z.string().nullish(),
    customText: z.string().nullish(),
    oaCode: z.string().nullish(),
    externalOrderId: z.string().nullish(),
    externalClientId: z.string().nullish(),
    alphabet: z.string().nullish(),
    productColor: z.string().nullish(),
    productSize: scalar,
    productId: z.string().nullish(),
    productLine: z.unknown().nullish(),
    purchaseStatus: z.union([z.boolean(), z.string()]).nullish(),
    notes: z.string().nullish(),
    storeCode: z.string().nullish(),
    storeRegion: z.string().nullish(),
    storeCountry: z.string().nullish(),
    client: z.record(z.string(), z.unknown()).nullish(),
    clientEmail: z.string().nullish(),
    clientPhone: z.string().nullish(),
    sfCartId: z.unknown().nullish(),
    shirtPdfAttachedToOrder: z.boolean().nullish(),
    otherPdfAttachedToOrder: z.boolean().nullish(),
    workshopName: z.string().nullish(),
    workshopType: z.string().nullish(),
    workshop: z.object({ name: z.string().nullish() }).loose().nullish(),
    storeName: z.string().nullish(),
    onHold: z.boolean().nullish(),
    receivedDateBlois: z.string().nullish(),
    shipmentDateBlois: z.string().nullish(),
    color: z.object({ name: z.string().nullish() }).loose().nullish(),
    product: orderProductSchema.nullish(),
    store: z.record(z.string(), z.unknown()).nullish(),
    props: z.record(z.string(), z.unknown()).nullish(),
    parts: z.array(orderPartSchema).nullish(),
    statusLogs: z.array(statusLogSchema).nullish(),
  })
  .loose();

export type Order = z.infer<typeof orderSchema>;
export type { Scalar };

const pageSchema = z.object({
  docs: z.array(orderSchema),
  totalDocs: z.number().nullish(),
  limit: z.number().nullish(),
  page: z.number().nullish(),
  totalPages: z.number().nullish(),
  hasPrevPage: z.boolean().nullish(),
  hasNextPage: z.boolean().nullish(),
  prevPage: z.number().nullish(),
  nextPage: z.number().nullish(),
  pagingCounter: z.number().nullish(),
});

export type OrderPage = z.infer<typeof pageSchema>;

/** `POST customization/query`: one page per requested status (`{ [statusId]: page }`). */
export const queryOrders = (params: {
  statuses: number[];
  pages: number[];
  storeIds: readonly StoreId[];
  platform: Platform;
  search: string;
}): Promise<Record<string, OrderPage>> => {
  const storeIds = params.storeIds.some((id) => Number(id) === STORE_ALL.id)
    ? ['all']
    : params.storeIds.map(String);
  // The query string is built by hand: an empty search still sends `search=`.
  const query = `search=${params.search}&statuses=${params.statuses.join(',')}&platform=${params.platform}&pages=${params.pages.join(',')}`;
  return api.post<Record<string, OrderPage>>(
    `customization/query?${query}`,
    { stores: storeIds.join(',') },
    { schema: z.record(z.string(), pageSchema) },
  );
};

/** `POST customization/query` with `report=true`: every order of the platform, for the Excel export. */
export const queryOrdersReport = (params: {
  storeIds: readonly StoreId[];
  platform: Platform;
}): Promise<Order[]> => {
  const storeIds = params.storeIds.some((id) => Number(id) === STORE_ALL.id)
    ? ['all']
    : params.storeIds.map(String);
  return api.post<Order[]>(
    `customization/query?search=&statuses=&platform=${params.platform}&report=true`,
    { stores: storeIds.join(',') },
    { schema: z.array(orderSchema) },
  );
};

/** `PUT customization`: the whole order; the API answers with `{ error }` on a status anomaly. */
export const updateOrder = (order: Order) =>
  api.put<{ error?: string }>('customization', order);

const userSchema = z.object({ id: z.string(), username: z.string().nullish() });
export type OrderUser = z.infer<typeof userSchema>;

/** `GET user`: the users, to name who validated a field in the details. */
export const fetchUsers = () =>
  api.get<OrderUser[]>('user', { schema: z.array(userSchema) });

/** `GET customization/:id`: one order with every field (the label page). */
export const fetchOrder = (id: string) =>
  api.get<Order>(`customization/${encodeURIComponent(id)}`, {
    schema: orderSchema,
  });

/** `PUT customization/:id/part`: a part status; the API answers with the whole order. */
export const updateOrderPart = (
  orderId: string,
  part: { id: string; customisationStatusId: number },
) =>
  api.put<Order>(`customization/${encodeURIComponent(orderId)}/part`, part, {
    schema: orderSchema,
  });

export type OrderPdfType = 'shirt' | 'other';

/** `GET pdf/read/:id/:type?format=pdf`: the purchase order generated by the API. */
export const fetchOrderPdf = (orderId: string, type: OrderPdfType) =>
  fetchBlob(
    `pdf/read/${encodeURIComponent(orderId)}/${type}?format=pdf`,
    'application/pdf',
  );

/** `PUT pdf/upload/:id/:type?serverGenerated=true`: the API generates and attaches the PDF. */
export const uploadOrderPdf = (orderId: string, type: 'SHIRT' | 'OTHER') =>
  api.put<unknown>(
    `pdf/upload/${encodeURIComponent(orderId)}/${type}`,
    undefined,
    { query: { serverGenerated: true } },
  );

const dmsItemFieldsSchema = z.array(
  z.object({ data: z.record(z.string(), z.unknown()).nullish() }),
);

/** The known fabric codes, to flag an unknown code on a part. */
export const fabricCodesQueryOptions = () =>
  queryOptions({
    queryKey: ['dms-items', 'fabric', 'data.code'] as const,
    queryFn: async () => {
      const items = await api.get('dms-item', {
        query: { type: 'fabric', fields: 'data.code' },
        schema: dmsItemFieldsSchema,
      });
      return new Set(
        items
          .map((item) => item.data?.code)
          .filter(
            (code): code is string => typeof code === 'string' && code !== '',
          ),
      );
    },
  });

/** The sizes offered by each model (`model` -> `"S,M,L"`). */
export const modelSizesQueryOptions = () =>
  queryOptions({
    queryKey: ['dms-items', 'model', 'data.model,data.sizes'] as const,
    queryFn: async () => {
      const items = await api.get('dms-item', {
        query: { type: 'model', fields: 'data.model,data.sizes' },
        schema: dmsItemFieldsSchema,
      });
      const sizes = new Map<string, string>();
      for (const item of items) {
        const model = item.data?.model;
        const list = item.data?.sizes;
        if (typeof model === 'string' && typeof list === 'string' && list)
          sizes.set(model, list);
      }
      return sizes;
    },
  });

export const usersQueryOptions = () =>
  queryOptions({ queryKey: ['users'] as const, queryFn: fetchUsers });
